// @ts-strict-ignore
import * as React from 'react';
import produce from 'immer';
import {clsx} from 'clsx';

import {ButtonSize, ButtonTheme} from 'common/components/button/constants';
import {SelectReact} from 'common/components/select';
import {SelectTheme} from 'common/components/select/constants';
import {ChangeEvent} from 'common/components/select/types';
import {QueryBlockReact} from 'common/query-builder/query-block';
import {
  IncludeCustomPropertyType,
  MenuItemType,
  SelectedPropertyItem,
} from 'common/query-builder/query-block/properties-menu/types';
import {SegmentValue} from 'common/query-builder/query-block/segment-menu/types';
import {segmentationToPropertySelection} from 'common/query-builder/query-block/segment-menu/util';
import {
  EntryPart,
  isEventBasedEntry,
  QuerySuperEntry,
  SelectorTypeAggregation,
} from 'common/query-builder/query-entry-section/types';
import {Behavior, Segmentation, WindowType} from 'common/types/reports/query';
import {SelectedEventPropertyItem} from 'common/query-builder/query-block/breakdown-menu';
import {EventItem, SelectEventItem} from 'common/query-builder/query-block/events-menu/types';
// eslint-disable-next-line import/no-cycle
import {RowFilters} from 'common/query-builder/query-entry-section/components/filters';
import {ReportName, SPECIAL_EVENTS} from 'common/report/constants';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {TimeUnitLabelTranslatable} from 'common/widgets/date-range-picker/models/types';
import {MultiAttributionLookback} from 'common/widgets/multi-attribution-lookback';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {SelectorType} from 'common/query-builder/query-block/constants';
import {MenuType} from 'common/query-builder/query-block/types';
import {CurrentProject} from 'common/report/model-resources/projects';
import {renameEvent} from 'common/report/util/events';
import {useTranslation} from 'common/i18n/hooks';
import {propertyItemToSegmentation} from 'common/query-builder/query-block/properties-menu/util';
import {RevenueChangeBlock} from 'common/query-builder/query-entry-section/components/revenue-change';
import {LocalizationStore} from 'common/i18n/store';
import {getPropertyName} from 'common/query-builder/query-block/count-menu/util';
import {PropertySectionItem} from 'common/query-builder/query-block/properties-menu/item-menu-types';
import {useCurrentProject} from 'common/contexts/react/current-project-context';

import {EntryRowProps} from '../types';
import {
  assignDefaultTimeReference,
  DEFAULT_AGGREGATION_BEHAVIOR,
  DEFAULT_FREQUENCY_BEHAVIOR,
  generateAggregationsList,
  getAggregationLabelForBehavior,
  getColumnLabelForBehavior,
  isAggregateUserFreqByGroupSegmentation,
  isAggregationBehavior,
  isAggregationSegmentation,
  isEventBasedBehavioralSegmentation,
  isMultiAttributionSegmentation,
} from './utils';
import {BehavioralStepSelectionSubItem} from './behavioral-step-selection-subitem';
import {BehavioralLookbackWindowSubItem} from './behavioral-lookback-window-subitem';

export type PropertySelectorProps<EntryType = QuerySuperEntry> = EntryRowProps<EntryType> & {
  displayCohorts?: boolean;
  // not ideal, but depending on `type` it can be 3 different things
  onSelect: (item: any, queryReason?: QueryReason, trackingProps?: Obj) => void;
  isPlaceholder?: boolean;
  isForFilter?: boolean;
  selected?: SegmentValue;
  selectorIcon?: string;
  isInner?: boolean;
  isForRevenue?: boolean;
  currentProject?: CurrentProject;
  tooltip?: Obj;
};

export function BehavioralTimeReference(props: PropertySelectorProps) {
  const currentSelection = props.selected as Segmentation;
  const currentWindowType = currentSelection.behavior.windowType;
  const aggUserFreqByGroup = isAggregateUserFreqByGroupSegmentation(currentSelection);

  const {reportName, stepCount, sectionName} = props.sectionProps;

  const {t} = useTranslation();

  const funnelTimeReferenceSelection = (
    <div className='subitem-select'>
      <BehavioralStepSelectionSubItem selected={currentSelection} stepCount={stepCount} onChange={props.onSelect} />
    </div>
  );

  const retentionTimeReferenceSelection = (
    <div className='subitem-select'>
      <BehavioralLookbackWindowSubItem
        lookbackWindow={currentSelection.behavior}
        open={props.activePart === EntryPart.BehavioralLookbackWindow}
        onOpen={() => props.setActivePart(EntryPart.BehavioralLookbackWindow, `open`)}
        onClose={() => props.setActivePart(EntryPart.BehavioralLookbackWindow, `close`)}
        onChange={(window) =>
          props.onSelect({
            ...currentSelection,
            behavior: {
              ...currentSelection.behavior,
              ...window,
            },
          })
        }
      />
    </div>
  );

  const handleTimeReferenceSelection = (ev: ChangeEvent) => {
    const {value: windowType} = ev.detail.item;
    const selection = {
      ...currentSelection,
      behavior: assignDefaultTimeReference(currentSelection.behavior, windowType),
    };
    props.onSelect(selection);
  };

  const getTimeReferenceLabel = (windowType: WindowType) => {
    switch (windowType) {
      case WindowType.BETWEEN:
        return t(`query-builder.behavior-interval.between-funnel-steps`);
      case WindowType.AFTER:
        return t(`query-builder.behavior-interval.after-step-1`);
    }
    return t(`query-builder.behavior-interval.per-interval`);
  };

  const getSectionItems = () => {
    const sectionItems = [
      {
        label: t(`query-builder.behavior-interval.per-interval`),
        value: WindowType.PER_INTERVAL,
        isSelected: !currentWindowType,
      },
      {
        label: t(`query-builder.behavior-interval.between-steps`),
        value: WindowType.BETWEEN,
        isSelected: currentWindowType === WindowType.BETWEEN,
      },
      {
        label: t(`query-builder.behavior-interval.after-step-1`),
        value: WindowType.AFTER,
        isSelected: currentWindowType === WindowType.AFTER,
      },
    ];

    if (sectionName === `inline-filter` || aggUserFreqByGroup) {
      // Inline filter only supports `Per Interval` - don't need to check metric type since it's only for simple metrics
      return sectionItems.filter((item) => item.value === WindowType.PER_INTERVAL);
    }

    return sectionItems;
  };

  if (!aggUserFreqByGroup && reportName === ReportName.Funnels) {
    return (
      <div className='subitem-text'>
        <div className='behavioral-time-reference'>{t(`query-builder.behavior-interval.between-funnel-steps`)}</div>
        {funnelTimeReferenceSelection}
      </div>
    );
  }
  if (!aggUserFreqByGroup && reportName === ReportName.Retention) {
    return (
      <div className='subitem-text'>
        <div className='behavioral-time-reference'>{t(`query-builder.behavior-interval.after-step-1`)}</div>
        {retentionTimeReferenceSelection}
      </div>
    );
  }

  return (
    <div className='subitem-text'>
      <SelectReact
        className='subitem-select time-reference-select'
        elref='timeReferenceSelect'
        disableSearch
        menuWidth={150}
        tabIndex={-1}
        theme={SelectTheme.QbSubitem}
        size={ButtonSize.Small}
        label={getTimeReferenceLabel(currentWindowType)}
        sections={[
          {
            items: getSectionItems(),
          },
        ]}
        onChange={handleTimeReferenceSelection}
        tooltip={props.tooltip}
      />
      {currentWindowType === WindowType.BETWEEN && funnelTimeReferenceSelection}
      {currentWindowType === WindowType.AFTER && retentionTimeReferenceSelection}
    </div>
  );
}

export function BehavioralSubItem(
  props: PropertySelectorProps & {
    contextPanelParentElement: HTMLElement;
    showBehaviorTimeReference?: boolean;
  },
) {
  const currentProject = useCurrentProject().get().value;
  const i18n = useTranslation();

  const currentSelection = props.selected as Segmentation;
  const behavior = currentSelection.behavior;
  const isEventProp = isAggregationBehavior(behavior);
  const isListOfObjectProp = isEventProp && !!props.sectionProps.listProperty;
  const isEvent = !isEventProp;

  const handleAggregationSelection = (ev: ChangeEvent<SelectorTypeAggregation>) => {
    const {value: aggregation} = ev.detail.item;
    const selection = addBehaviorToSegmentation(
      currentSelection,
      {
        ...behavior,
        aggregationOperator: aggregation.operator,
      },
      i18n,
    );
    props.onSelect({...currentSelection, ...selection});
  };

  const sections = generateAggregationsList(behavior);
  const label = getAggregationLabelForBehavior(behavior);

  return (
    <div className='mp-query-sub-selector'>
      {isAggregationBehavior(behavior) && (
        <div className='subitem-text'>
          {/* aggregation selection */}
          <SelectReact
            className='subitem-select'
            elref='aggregatorSelect'
            disableSearch
            tabIndex={-1}
            theme={SelectTheme.QbSubitem}
            size={ButtonSize.Small}
            open={props.activePart === EntryPart.Aggregator}
            label={label}
            sections={sections}
            onClose={() => props.setActivePart(EntryPart.Aggregator, `close`)}
            onOpen={() => props.setActivePart(EntryPart.Aggregator, `open`)}
            onChange={handleAggregationSelection}
          />
        </div>
      )}
      {isEvent && (
        <PropertySelector
          {...props}
          selected={props.selected}
          displayCohorts={props.displayCohorts}
          isForFilter={props.isForFilter}
          isInner
          activePart={props.activePart}
          setActivePart={props.setActivePart}
          queryBlockConfig={{
            ...props.queryBlockConfig,
            type: MenuType.Events,
            selected: behavior?.event ?? {custom: false, label: ``, value: null},
          }}
          onSelect={(selectedItem: SelectEventItem) => {
            const segment: Segmentation = props.selected as Segmentation;
            const handledEvent = getPropertySelectionFromEventUpdate(selectedItem, segment, i18n);
            props.onSelect(handledEvent);
          }}
        />
      )}
      {isEventProp && !isListOfObjectProp && (
        <PropertySelector
          {...props}
          selected={props.selected}
          displayCohorts={props.displayCohorts}
          isForFilter={props.isForFilter}
          isInner
          activePart={props.activePart}
          setActivePart={props.setActivePart}
          queryBlockConfig={{
            ...props.queryBlockConfig,
            type: MenuType.Breakdown,
            showSpecialDatePropertyUnit: true,
            selected: {
              event: behavior?.event ?? {custom: false, label: ``, value: null},
              property: behavior?.property,
            },
          }}
          onSelect={(selectedItem: SelectedEventPropertyItem) => {
            const segment: Segmentation = props.selected as Segmentation;
            const handledEvent = getPropertySelectionFromEventPropUpdate(selectedItem, segment, i18n);
            props.onSelect(handledEvent);
          }}
        />
      )}

      {props.showBehaviorTimeReference !== false && (
        <BehavioralTimeReference
          {...props}
          selected={props.selected}
          onSelect={props.onSelect}
          activePart={props.activePart}
          setActivePart={props.setActivePart}
          currentProject={currentProject}
        />
      )}
    </div>
  );
}

export function MultiAttributionLookbackSubItem(props: PropertySelectorProps) {
  const {t, print} = useTranslation();
  if (!isMultiAttributionSegmentation(props.selected)) {
    return null;
  }
  const {window} = props.selected.behavior.dateRange;
  const dateRangeLabel = t(`query-builder.attribution.lookback-duration`, {
    duration: `${window.value} ${print(TimeUnitLabelTranslatable[window.unit](1))}`,
  });
  const isOpen = props.activePart === EntryPart.MultiAttributionLookback;
  return (
    <div className='subitem-text'>
      <SelectReact
        className='subitem-select'
        elref='multiAttributionLookbackSelect'
        theme={SelectTheme.QbSubitem}
        size={ButtonSize.Small}
        label={dateRangeLabel}
        open={isOpen}
        onMenuChange={(ev) =>
          props.setActivePart(EntryPart.MultiAttributionLookback, ev.detail.isOpen ? `open` : `close`)
        }
      >
        <div slot='body'>
          {isOpen && (
            <MultiAttributionLookback
              lookbackWindow={props.selected.behavior.dateRange.window}
              projectId={props.sectionProps.projectId}
              onCancel={() => props.setActivePart(EntryPart.MultiAttributionLookback, `close`)}
              onChange={(lookbackWindow) => {
                const updatedSelection = produce(props.selected as Segmentation, (draft) => {
                  draft.behavior.dateRange.window = {...lookbackWindow};
                });
                props.onSelect(updatedSelection);
              }}
            />
          )}
        </div>
      </SelectReact>
    </div>
  );
}

/**
 * Ensures all of the fields of the behavior are in sync (such as the propertyName)
 */
function addBehaviorToSegmentation(
  segmentation: Segmentation,
  behavior: Behavior,
  i18n: LocalizationStore,
): Segmentation {
  const defaultBehavior = isAggregationBehavior(behavior) ? DEFAULT_AGGREGATION_BEHAVIOR : DEFAULT_FREQUENCY_BEHAVIOR;
  return produce(segmentation, (draft) => {
    const behaviorDraft = {
      ...defaultBehavior,
      ...draft.behavior,
      ...behavior,
    } as Behavior;
    draft.behavior = behaviorDraft;

    draft.propertyName = i18n.print(getColumnLabelForBehavior(behaviorDraft));
  });
}

function getPropertySelectionFromEventPropUpdate(
  selectedEventPropertyItem: SelectedEventPropertyItem,
  currentSelection: Segmentation,
  i18n: LocalizationStore,
) {
  const {event, property} = selectedEventPropertyItem.item;
  return addBehaviorToSegmentation(currentSelection, {...currentSelection.behavior, event, property}, i18n);
}

function getPropertySelectionFromEventUpdate(
  eventItem: SelectEventItem,
  currentSelection: Segmentation,
  i18n: LocalizationStore,
) {
  const item = eventItem.item;
  const event: EventItem = item as EventItem;
  const overrides: Partial<Behavior> = {};

  // The special "Selected Step" event should come with a starting step selection
  if (event.value === SPECIAL_EVENTS.SELECTED_STEP) {
    overrides.startStepNumber = 0;
  }

  return addBehaviorToSegmentation(
    currentSelection,
    {
      ...currentSelection.behavior,
      event: {label: event.label, value: event.value},
      ...overrides,
    },
    i18n,
  );
}

function getInnerLabelRenderer(segment: SegmentValue) {
  const {print} = useTranslation();
  if (isMultiAttributionSegmentation(segment) || !isAggregationSegmentation(segment) || !segment?.behavior.property) {
    return null;
  }

  const behavior = segment.behavior;
  const event = renameEvent(behavior?.event?.label.toString());
  const propertyName = getPropertyName(behavior.property, {
    includeListItemGroup: true,
    showSpecialDatePropertyUnit: true,
  });

  return () => (
    <>
      {print(propertyName)}
      <span className='regular'> on </span>
      {event}
    </>
  );
}

/**
 * Query block selector for breakdowns & filters
 */
export function PropertySelector(
  props: PropertySelectorProps & {
    contextPanelParentElement: HTMLElement;
    otherFilterOptions?: Array<PropertySectionItem<string>>;
  },
) {
  const {t, print} = useTranslation();

  const entryPart = props.isInner ? EntryPart.SubValue : EntryPart.Value;
  const labelRenderer = props.isInner ? getInnerLabelRenderer(props.selected) : null;

  // by default shows the property menu
  return (
    <QueryBlockReact
      className={clsx(`entry-selector`, {
        'is-disabled': props.entry.readOnlyEvent,
        'is-placeholder': props.isPlaceholder && props.selected === null,
        'subitem-style': props.isInner,
      })}
      showSessionEvent
      selected={props.isForFilter ? {...props.selected} : segmentationToPropertySelection(props.selected)}
      elref='entrySelector'
      applyButtonText={
        props.sectionProps.sectionTitle && t(`query-builder.add-item`, {item: print(props.sectionProps.sectionTitle)})
      }
      disableValueSelection
      minSelection={1}
      prefixLabel={props.entry.prefixLabel ?? undefined}
      allDataGroups={props.sectionProps.dataGroupsEnabled}
      cohortMultiselect
      dataGroupId={props.sectionProps.dataGroupId}
      metricLevelDataGroups={props.sectionProps.metricLevelDataGroups}
      disabledItems={props.sectionProps.disabledItems}
      enableEnhancedListFilters={props.sectionProps.enableEnhancedListFilters}
      enableWorkspaceFilterRestrictions={props.sectionProps.enableWorkspaceFilterRestrictions}
      eventNames={props.sectionProps.eventNames}
      excludeCustomEvents={props.sectionProps.excludeCustomEvents}
      excludeDimensionProperties={props.sectionProps.excludeDimensionProperties}
      hideEditControls={props.sectionProps.hideEditControls}
      hideEvents={props.sectionProps.hideEvents}
      includeCustomProperties={props.sectionProps.includeCustomProperties}
      showBorrowedProperties={props.sectionProps.showBorrowedProperties}
      inlineCohortsOverride={props.sectionProps.inlineCohortsOverride}
      isMultiAttribution={isMultiAttributionSegmentation(props.selected)}
      projectHasCohorts={props.displayCohorts && props.sectionProps.projectHasCohorts}
      projectHasPeople={props.sectionProps.projectHasPeople}
      projectId={props.sectionProps.projectId}
      projectIsFreePlan={props.sectionProps.projectIsFreePlan}
      propertyCombineMode={props.sectionProps.propertyCombineMode ?? undefined}
      readonly={props.sectionProps.readonly}
      selectorIcon={props.selectorIcon ?? ``}
      selectorType={SelectorType.Button}
      showSpecialDatasetProperty={props.sectionProps.showSpecialDatasetProperty}
      supportB2bCohorts={props.sectionProps.supportB2bCohorts}
      trackName={props.sectionProps.trackName}
      userId={props.sectionProps.userId}
      workspaceId={props.sectionProps.workspaceId}
      autofocus={props.index === 0 && props.sectionProps.autofocus && props.isPlaceholder && !props.isFirstEntryStaged}
      disabled={props.sectionProps.disabled}
      open={props.isPlaceholder && props.activePart === entryPart}
      showAllTab
      buttonSize={props.isInner && props.hasNewUI ? ButtonSize.Small : ButtonSize.Medium}
      theme={
        props.hasNewUI
          ? ButtonTheme.QueryBuilder
          : props.entry.readOnlyEvent
            ? ButtonTheme.Tertiary
            : ButtonTheme.Select
      }
      menuItemTypes={props.sectionProps.propertiesMenuItemTypes}
      typeFilter={props.sectionProps.propertiesMenuPropertyTypes}
      {...props.queryBlockConfig}
      labelRenderer={labelRenderer}
      contextPanelParentElement={props.contextPanelParentElement}
      onClose={() => {
        props.setActivePart(entryPart, `close`);
        props.onEntrySelectorDropdownStateChanged(false);
      }}
      onOpen={() => {
        props.setActivePart(entryPart, `open`);
        props.onEntrySelectorDropdownStateChanged(true);
      }}
      onRemove={() => props.removeEntry()}
      onTrack={(ev: CustomEvent) => props.trackEntrySelectorEvent(ev)}
      onSelect={props.onSelect}
    />
  );
}

/**
 * Renders event only filters for used in context of a behavior inline filters
 */
export function BehavioralSubFilters(props: PropertySelectorProps) {
  if (!isEventBasedBehavioralSegmentation(props.selected)) {
    return null;
  }

  const behavior = props.selected.behavior;

  return (
    <RowFilters
      entry={props.entry}
      eventNames={behavior.event?.value ? [behavior.event.value] : []}
      index={props.index}
      filters={behavior.filters?.map((segmentation) => new PropertyFilter(segmentation)) || []}
      selectedFiltersOperator={behavior.filtersOperator}
      extraClass={{'for-behaviors': true}}
      onUpdate={(filters, filtersOperator, queryReason) =>
        props.onSelect(
          produce(props.selected as Segmentation, (item) => {
            item.behavior.filters = filters.map((filter) => filter.getAttributes());
            item.behavior.filtersOperator = filtersOperator;
          }),
          queryReason,
        )
      }
      sectionParams={{
        dataGroupsEnabled: false,
        allowCohortFilter: false,
        canRenameStep: false,
        projectHasPeople: false,
        projectHasCohorts: false,
        hideEvents: false,
        includeCustomProperties: IncludeCustomPropertyType.All,
      }}
      hideEvents={!isEventBasedEntry(props.entry)}
      hasNewUI={props.hasNewUI}
      sectionProps={props.sectionProps}
      nestedSectionParams={props.nestedSectionParams}
      placeholders={props.placeholder}
      onAbortStage={() => props.setActivePart(EntryPart.Filters, `close`)}
    />
  );
}

export function RevenueSubItem(
  props: PropertySelectorProps & {
    contextPanelParentElement: HTMLElement;
  },
) {
  const selected = props.selected as Segmentation;
  return (
    <div className='mp-query-sub-selector'>
      <PropertySelector
        {...props}
        isInner
        contextPanelParentElement={props.contextPanelParentElement}
        onSelect={(selection: SelectedPropertyItem) => {
          const handledEvent = {
            ...selected,
            behavior: {
              ...selected.behavior,
              property: selection.item,
            },
          };
          props.onSelect(handledEvent);
        }}
        isPlaceholder={props.isPlaceholder}
        queryBlockConfig={{
          ...props.queryBlockConfig,
          type: MenuType.Properties,
          hideEvents: true,
          menuItemTypes: [MenuItemType.SCDProperty],
          otherGroupByOptions: [],
          otherFilterOptions: [],
          showAllTab: false,
          supportB2bCohorts: false,
        }}
        selected={propertyItemToSegmentation(selected.behavior.property, selected.behavior.property?.resourceType)}
      />
      {props.hasNewUI && (
        <RevenueChangeBlock
          behavior={selected.behavior}
          onChange={(updatedBehavior) => {
            props.onSelect({
              ...selected,
              behavior: updatedBehavior,
            });
          }}
        />
      )}
    </div>
  );
}
